import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  Building, 
  TrendingUp, 
  Database, 
  Activity, 
  CheckCircle, 
  RefreshCw, 
  Coins, 
  ArrowUpRight, 
  Heart, 
  Scale, 
  Cloud,
  FileCheck,
  Smartphone,
  ChevronRight,
  ShieldCheck,
  Lock
} from 'lucide-react';

interface DealRecord {
  id: string;
  clientName: string;
  companyName: string;
  contactEmail: string;
  contactPhone: string;
  serviceSold: string;
  dealValue: string;
  executionNode: string;
  status: string;
  date: string;
  ownerId: string;
}

export function SecurePaymentLiveBalancePipelineNode() {
  const [deals, setDeals] = useState<DealRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastSync, setLastSync] = useState<Date>(new Date());
  const [showDirectTransfer, setShowDirectTransfer] = useState(false);

  // Parse deal value string to float accurately (e.g., "£120,000" or "85000")
  const parseDealValue = (valStr: string): number => {
    if (!valStr) return 0;
    const cleaned = valStr.replace(/[^0-9.]/g, '');
    return parseFloat(cleaned) || 0;
  };

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'deals'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dealsData: DealRecord[] = [];
      snapshot.forEach((doc) => {
        dealsData.push({ id: doc.id, ...doc.data() } as DealRecord);
      });
      setDeals(dealsData);
      setLastSync(new Date());
      setLoading(false);
    }, (err) => {
      console.error("Firestore deal listener error inside SecurePayment node tracker:", err);
      setError('Live connection to Firestore deals node failed.');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Compute live values from Firestore
  const settledDeals = deals.filter(d => d.status === 'SETTLED');
  const pipelineDeals = deals.filter(d => d.status !== 'SETTLED');

  const liveGrossSettled = settledDeals.reduce((sum, d) => sum + parseDealValue(d.dealValue), 0);
  const livePipelineValue = pipelineDeals.reduce((sum, d) => sum + parseDealValue(d.dealValue), 0);

  // Multi-Stage NexoraX Financial Protocol Math:
  // 1. Calculate 1% 3% Community Allocation
  const liveNexoraXLevy = liveGrossSettled * 0.01;
  const splitDunfermline = liveNexoraXLevy / 3;
  const splitGlobal = liveNexoraXLevy / 3;
  const splitUK = liveNexoraXLevy / 3;

  // 2. 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) post levy
  const activeRemainder = liveGrossSettled - liveNexoraXLevy;
  const liveLifetimeTax = activeRemainder * 0.20;

  // 3. Net Settlement routed to Secure Payment Provider Sort Verified Payment Configuration Acc Secure Billing Provider
  const liveNetSettled = activeRemainder - liveLifetimeTax;

  // 4. Cumulative SecurePayment Balance (Initial Reserve base of £2,227,800 + live net settlements)
  const initialBaseReserves = 2227800.00;
  const cumulativeSecurePaymentBalance = initialBaseReserves + liveNetSettled;

  return (
    <div className="w-full space-y-6 text-left">
      {/* religious preamble title block */}
      <div className="bg-[#030612]/75 border border-amber-500/15 rounded-xl px-4 py-2.5">
        <span className="text-[10px] font-sans text-amber-500/90 font-bold block select-text">
          In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Secure Payment Provider Live Reserves Card */}
        <div className="lg:col-span-8 bg-gradient-to-br from-[#040918] via-[#02050c] to-[#010204] border border-cyan-500/30 rounded-2xl p-6 shadow-[0_0_40px_rgba(6,182,212,0.1)] relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none"></div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-start border-b border-white/5 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-mono font-bold tracking-[0.25em] text-cyan-400 uppercase">
                    NODE-07 // SecurePayment OPEN BANKING HUB
                  </span>
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                </div>
                <h3 className="text-xl font-sans font-black text-white tracking-tight uppercase mt-1">
                  NexoraX SecurePayment Cash Reserves
                </h3>
              </div>

              <div className="text-right font-mono">
                <span className="text-[8px] text-slate-500 block font-bold">CLEARING INGRESS NODE</span>
                <span className="text-[9px] text-emerald-400 font-bold uppercase tracking-wider bg-emerald-950/20 px-2 py-0.5 rounded border border-emerald-500/10">
                  SORT: Verified Payment Configuration // ACC: Secure Billing Provider
                </span>
              </div>
            </div>

            {/* Balances Display */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-2">
              <div className="md:col-span-7 space-y-1">
                <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest block font-bold">
                  TOTAL SECURE AVAILABLE LIQUIDITY (SecurePayment BASE + LIVE SETTLED COIN)
                </span>
                
                {loading ? (
                  <div className="h-10 flex items-center gap-2 font-mono text-xs text-slate-500 animate-pulse">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-cyan-500" />
                    Calculating real-time database liquidity...
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl md:text-5xl font-mono font-black text-white tracking-tight drop-shadow-[0_0_15px_rgba(255,255,255,0.08)]">
                      £{cumulativeSecurePaymentBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-xs font-mono text-slate-400 uppercase font-black tracking-widest">GBP</span>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-2 text-[9px] font-mono text-slate-400 uppercase">
                  <span>Initial System Reserves: £{initialBaseReserves.toLocaleString()}</span>
                  <span className="text-slate-600 font-bold">|</span>
                  <span className="text-emerald-400 font-bold">Live Ingress: +£{liveNetSettled.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* Live Pipeline Column */}
              <div className="md:col-span-5 bg-black/40 border border-white/5 p-4 rounded-xl flex flex-col justify-between text-left relative overflow-hidden">
                <div className="absolute -right-2 -bottom-2 w-20 h-20 bg-indigo-500/5 blur-2xl rounded-full"></div>
                <div>
                  <span className="text-[9px] font-mono text-indigo-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5" /> STAGED REVENUE PIPELINE
                  </span>
                  <span className="text-2xl font-mono font-black text-slate-200 block mt-1.5">
                    £{livePipelineValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="text-[8px] font-mono text-slate-500 uppercase flex justify-between items-center border-t border-white/5 pt-2 mt-4">
                  <span>{pipelineDeals.length} ACTIVE CONTRACT AGREEMENTS</span>
                  <span className="text-indigo-400 font-bold">UNCAPTURED FLUX</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-4 mt-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-2 text-[8px] font-mono text-slate-500 uppercase leading-normal">
              <Cloud className="w-3.5 h-3.5 text-indigo-400" />
              <span>
                PERSISTENT MIRROR: /var/www/nexorax_source/ snaps verified by CloudSync-02 to NexoraX Resource Library.
              </span>
            </div>

            <div className="flex items-center gap-2 shrink-0 font-mono text-[9px]">
              <span className="text-slate-500 uppercase font-bold">
                LAST SYNC: {lastSync.toLocaleTimeString()}
              </span>
              <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Real-Time Formula Metrics */}
        <div className="lg:col-span-4 bg-[#050914] border border-white/5 rounded-2xl p-5 shadow-2xl flex flex-col justify-between space-y-4">
          <div className="space-y-3 text-left w-full">
            <div className="flex justify-between items-center border-b border-white/5 pb-2.5">
              <h4 className="text-xs font-bold text-amber-500 font-mono uppercase tracking-widest flex items-center gap-1.5">
                <Coins className="w-3.5 h-3.5" /> ACCOUNTING ALLOCATIONS
              </h4>
              <span className="text-[8px] font-mono text-slate-500 uppercase font-black">LEVY & TAX CORE</span>
            </div>

            <div className="space-y-2.5 font-mono text-[10px] text-slate-300">
              <div className="flex justify-between items-center bg-black/40 border border-slate-900/60 px-3 py-2 rounded-lg">
                <span className="text-[9px] text-slate-400 font-bold uppercase">GROSS DEALS CAPTURED:</span>
                <span className="text-white font-black tracking-wider">£{liveGrossSettled.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              <div className="flex justify-between items-center bg-black/40 border border-slate-900/60 px-3 py-2 rounded-lg">
                <span className="text-[9px] text-amber-500 font-bold uppercase flex items-center gap-1">
                  <Heart className="w-3 h-3 text-amber-500" /> 1% 3% Community Allocation:
                </span>
                <span className="text-amber-400 font-black tracking-wider">£{liveNexoraXLevy.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              <div className="flex justify-between items-center bg-black/40 border border-slate-900/60 px-3 py-2 rounded-lg">
                <span className="text-[9px] text-purple-400 font-bold uppercase flex items-center gap-1">
                  <Scale className="w-3 h-3 text-purple-400" /> 10% Maintenance & Hosting Fee:
                </span>
                <span className="text-purple-300 font-black tracking-wider">£{liveLifetimeTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              <div className="flex justify-between items-center bg-[#022a16]/10 border border-[#10b981]/20 px-3 py-2 rounded-lg text-emerald-400 font-extrabold">
                <span className="text-[9px] uppercase">NET SecurePayment INGRESS COIN:</span>
                <span>£{liveNetSettled.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            {/* 1% split details */}
            <div className="bg-black/40 border border-white/5 rounded-xl p-3 space-y-1 text-[8.5px] font-mono text-slate-400 uppercase leading-relaxed">
              <div className="text-[9px] text-amber-500 font-bold border-b border-white/5 pb-1 mb-1 flex justify-between">
                <span>LEVY 3-WAY DISPATCH (33.33% SPLIT)</span>
                <span>STATUS: ROUTING</span>
              </div>
              <div className="flex justify-between">
                <span>Dunfermline Node:</span>
                <span className="text-amber-300 font-bold">£{splitDunfermline.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span>Global Mission Node:</span>
                <span className="text-amber-300 font-bold">£{splitGlobal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span>United Kingdom Node:</span>
                <span className="text-amber-300 font-bold">£{splitUK.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          <div className="text-[8px] font-mono text-slate-500 uppercase leading-normal border-t border-white/5 pt-3 w-full text-center">
            LEDI INTEGRATION VERIFIED DIRECTLY BY THE EXECUTIVE OFFICE.
          </div>
        </div>
      </div>

      {/* Settled versus active deal breakdown ledger */}
      <div className="bg-[#050914] border border-slate-800/80 rounded-2xl p-5 shadow-2xl space-y-4">
        <div className="flex justify-between items-center border-b border-white/5 pb-3">
          <h4 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
            <FileCheck className="w-4 h-4" /> DIRECT FLUX LEDGER: SETTLED INGRESS VS ACTIVE CONTRACTS
          </h4>
          <span className="text-[8px] font-mono text-slate-500 uppercase font-black">Real-Time Sync active</span>
        </div>

        {loading ? (
          <div className="py-6 text-center text-slate-500 font-mono text-xs animate-pulse">
            Retrieving raw transactional ledger telemetry...
          </div>
        ) : deals.length === 0 ? (
          <div className="py-6 text-center text-slate-500 font-mono text-xs uppercase border border-dashed border-slate-800 rounded-xl">
            No registered covenants detected on current nodes.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-60 overflow-y-auto custom-scrollbar">
            {/* Settled Ledger column */}
            <div className="space-y-2 text-left">
              <span className="text-[9px] font-mono text-emerald-400 font-bold uppercase tracking-wider block">
                🏦 Live Settlements (Sort: Verified Payment Configuration // Acc: Secure Billing Provider)
              </span>
              <div className="space-y-2">
                {settledDeals.length === 0 ? (
                  <div className="p-3 bg-black/20 border border-slate-900 text-center font-mono text-[9px] text-slate-600 uppercase rounded-xl">
                    No settled bank transfers verified yet.
                  </div>
                ) : (
                  settledDeals.map(d => (
                    <div key={d.id} className="p-3 bg-[#022c15]/10 border border-[#10b981]/20 rounded-xl flex justify-between items-center text-[10px] font-mono">
                      <div>
                        <span className="text-white font-extrabold block truncate max-w-[150px]">{d.companyName}</span>
                        <span className="text-slate-500 block text-[8px] uppercase">{d.serviceSold}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-emerald-400 font-black block">+{d.dealValue}</span>
                        <span className="text-[8px] text-slate-500 bg-emerald-950/40 px-1 py-0.5 rounded border border-[#10b981]/10 font-bold uppercase">SETTLED & ROUTED</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Pipeline Ledger column */}
            <div className="space-y-2 text-left">
              <span className="text-[9px] font-mono text-indigo-400 font-bold uppercase tracking-wider block">
                📈 Pipeline Contract Flux
              </span>
              <div className="space-y-2">
                {pipelineDeals.length === 0 ? (
                  <div className="p-3 bg-black/20 border border-slate-900 text-center font-mono text-[9px] text-slate-600 uppercase rounded-xl">
                    No active contract agreements staged in pipeline.
                  </div>
                ) : (
                  pipelineDeals.map(d => (
                    <div key={d.id} className="p-3 bg-indigo-950/10 border border-indigo-500/15 rounded-xl flex justify-between items-center text-[10px] font-mono">
                      <div>
                        <span className="text-white font-extrabold block truncate max-w-[150px]">{d.companyName}</span>
                        <span className="text-slate-500 block text-[8px] uppercase">{d.serviceSold}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-indigo-400 font-black block">{d.dealValue}</span>
                        <span className="text-[8px] text-slate-500 bg-indigo-950/20 px-1 py-0.5 rounded border border-indigo-500/10 font-bold uppercase">{d.status}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
